import { createStore } from "zustand/vanilla";
import type { StoreApi } from "zustand/vanilla";
import type { AccessLevel, ForteXApi, UserDto, UserFeatureAccess } from "@fortex/api";

export const authStorageKey = "fortex-web:auth";

export const features = {
  masterData: "FEATURE_MASTERDATA",
  geoObject: "FEATURE_GEOOBJECT",
  mapService: "FEATURE_MAPSERVICE",
  userAdmin: "FEATURE_USERADMIN",
  role: "FEATURE_ROLE",
  publicMap: "FEATURE_PUBLICMAP"
} as const;

export const uiFeatures = {
  menu: "FEATURE_UI_MENU",
  search: "FEATURE_UI_SEARCH",
  layers: "FEATURE_UI_LAYERS",
  grid: "FEATURE_UI_GRID",
  detail: "FEATURE_UI_DETAIL",
  photos: "FEATURE_UI_PHOTOS",
  route: "FEATURE_UI_ROUTE",
  measure: "FEATURE_UI_MEASURE",
  locate: "FEATURE_UI_LOCATE",
  coordinates: "FEATURE_UI_COORDINATES",
  legend: "FEATURE_UI_LEGEND",
  miniMap: "FEATURE_UI_MINIMAP"
} as const;

export type FeatureName = (typeof features)[keyof typeof features];

export type AuthSession = {
  token: string;
  user: UserDto;
  featureAccess: Record<string, AccessLevel>;
  staySignedIn: boolean;
};

export type AuthState = {
  token: string | null;
  user: UserDto | null;
  featureAccess: Record<string, AccessLevel>;
  staySignedIn: boolean;
};

export type AuthActions = {
  signIn(api: ForteXApi, email: string, password: string, staySignedIn: boolean): Promise<UserDto>;
  setSession(session: AuthSession): void;
  logout(): void;
  can(feature: FeatureName | string, level: Exclude<AccessLevel, "None">): boolean;
};

export type AuthStore = StoreApi<AuthState & AuthActions>;

export type AuthStorage = { session: Storage | null; local: Storage | null };

const levelRank: Record<AccessLevel, number> = { None: 0, ReadOnly: 1, Full: 2 };

export function accessMap(access: UserFeatureAccess | null | undefined): Record<string, AccessLevel> {
  const result: Record<string, AccessLevel> = {};

  for (const item of access?.features ?? []) result[item.feature] = item.accessLevel;

  return result;
}

export function grantedLevel(featureAccess: Record<string, AccessLevel>, feature: string, level: Exclude<AccessLevel, "None">): boolean {
  return levelRank[featureAccess[feature] ?? "None"] >= levelRank[level];
}

export function hasAccess(state: Pick<AuthState, "token" | "user" | "featureAccess">, feature: string, level: Exclude<AccessLevel, "None">): boolean {
  if (state.token === null) return false;
  if (state.user?.admin) return true;

  return grantedLevel(state.featureAccess, feature, level);
}

function browserStorage(): AuthStorage {
  if (typeof window === "undefined") return { session: null, local: null };

  try {
    return { session: window.sessionStorage, local: window.localStorage };
  } catch {
    return { session: null, local: null };
  }
}

function readSession(storage: Storage | null, staySignedIn: boolean): AuthSession | null {
  if (storage === null) return null;

  try {
    const raw = storage.getItem(authStorageKey);

    if (raw === null) return null;

    const parsed = JSON.parse(raw) as Partial<AuthSession>;

    if (typeof parsed.token !== "string" || parsed.token.length === 0 || !parsed.user || typeof parsed.user !== "object") return null;

    return { token: parsed.token, user: parsed.user, featureAccess: parsed.featureAccess && typeof parsed.featureAccess === "object" ? parsed.featureAccess : {}, staySignedIn };
  } catch {
    return null;
  }
}

function writeSession(storage: AuthStorage, session: AuthSession | null): void {
  const clear = (target: Storage | null) => {
    try {
      target?.removeItem(authStorageKey);
    } catch {
      return;
    }
  };

  clear(storage.session);
  clear(storage.local);

  if (session === null) return;

  const target = session.staySignedIn ? storage.local : storage.session;

  try {
    target?.setItem(authStorageKey, JSON.stringify({ token: session.token, user: session.user, featureAccess: session.featureAccess }));
  } catch {
    return;
  }
}

export function restoreSession(storage: AuthStorage = browserStorage()): AuthSession | null {
  return readSession(storage.local, true) ?? readSession(storage.session, false);
}

export function createAuthStore(storage: AuthStorage = browserStorage()): AuthStore {
  const restored = restoreSession(storage);

  return createStore<AuthState & AuthActions>()((set, get) => ({
    token: restored?.token ?? null,
    user: restored?.user ?? null,
    featureAccess: restored?.featureAccess ?? {},
    staySignedIn: restored?.staySignedIn ?? false,
    setSession: session => {
      writeSession(storage, session);
      set({ token: session.token, user: session.user, featureAccess: session.featureAccess, staySignedIn: session.staySignedIn });
    },
    signIn: async (api, email, password, staySignedIn) => {
      const response = await api.login(email, password);
      let featureAccess: Record<string, AccessLevel> = {};

      get().setSession({ token: response.bearer, user: response.user, featureAccess, staySignedIn });

      try {
        featureAccess = accessMap(await api.featureAccess(response.user.id));
      } catch {
        featureAccess = {};
      }

      get().setSession({ token: response.bearer, user: response.user, featureAccess, staySignedIn });

      return response.user;
    },
    logout: () => {
      writeSession(storage, null);
      set({ token: null, user: null, featureAccess: {}, staySignedIn: false });
    },
    can: (feature, level) => hasAccess(get(), feature, level)
  }));
}
